import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  PaperPlaneTilt, Receipt, Storefront, ShieldCheck, ArrowRight, Users,
  HandHeart, ChartLineUp,
  QrCode, Envelope, UserPlus, ChartBar,
  Bell, Lock, CreditCard
} from '@phosphor-icons/react';

import HeroBanner from '../components/HeroBanner';
import CrowdfundHero from '../components/CrowdfundHero';
import { FeatureGate } from '../context/FeatureFlagsContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

const FEATURES = [
  { icon: PaperPlaneTilt, title: 'Send & Receive Money', desc: 'Transfer funds instantly to anyone with just their email or $username.' },
  { icon: Receipt, title: 'Request Payments', desc: 'Create payment requests and get notified when they\'re fulfilled.' },
  { icon: Storefront, title: '8 Listing Types', desc: 'Products, services, subscriptions, crowdfunding, offers, tips, downloads, bookings.' },
  { icon: ChartLineUp, title: 'DiviSpend Investing', desc: 'Enable investor support on any listing. Fans invest and earn a % of every sale.' },
  { icon: HandHeart, title: 'Crowdfunding', desc: 'Launch campaigns for any reason and let your community back you.' },
  { icon: Users, title: 'Follow & Discover', desc: 'Follow vendors, see their feed, and explore the marketplace.' },
  { icon: QrCode, title: 'QR Codes & Pay Links', desc: 'Share QR codes and custom payment links for instant checkout.' },
  { icon: ChartBar, title: 'Seller Analytics & CRM', desc: 'Track revenue, top products, customer data, and broadcast updates.' },
  { icon: Envelope, title: 'Email Receipts', desc: 'Automatic styled receipts sent to buyer and seller on every transaction.' },
  { icon: Bell, title: 'Push Notifications', desc: 'Get notified of payments, messages, and offers in real time.' },
  { icon: UserPlus, title: 'Invite & Refer', desc: 'Share referral links via email, WhatsApp, SMS, and QR code.' },
  { icon: Lock, title: 'Security Suite', desc: '2FA, fraud detection, audit logs, GDPR compliance — enterprise-grade security.' },
];

export default function LandingPage() {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-[#111111]">
      {/* Header */}
      <header className="bg-[#111111]/80 backdrop-blur-xl border-b border-[#222222] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Divispend" className="h-8 sm:h-10 w-auto" />
          </Link>
          <nav className="flex items-center gap-3">
            {isAuthenticated ? (
              <Link to="/dashboard"><Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm" data-testid="dashboard-nav-button">Dashboard <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost" className="text-[#999999] hover:text-white hover:bg-[#333333] rounded-sm" data-testid="login-nav-button">Sign in</Button></Link>
                <Link to="/register"><Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm" data-testid="register-nav-button">Get Started</Button></Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Welcome / Splash — chunky pill aesthetic */}
      <section className="bg-black py-16 sm:py-24" data-testid="welcome-splash">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <img src={LOGO_URL} alt="Divispend" className="h-32 sm:h-40 w-auto mx-auto mb-10 drop-shadow-[0_0_20px_rgba(34,197,94,0.3)]" />
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#22C55E] leading-tight mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Let&apos;s Get Started
          </h1>
          <p className="text-white/80 text-base sm:text-lg leading-relaxed mb-10 max-w-md mx-auto">
            Divispend is the all-in-one marketplace where anyone can sell, send and receive money, and build their hustle from a single $username.
          </p>
          {isAuthenticated ? (
            <Link to="/dashboard">
              <button className="border-[3px] border-[#22C55E] text-[#22C55E] rounded-full px-12 py-4 text-lg font-bold tracking-tight active:bg-[#22C55E]/10 transition-colors" data-testid="splash-dashboard-btn">
                Open Dashboard
              </button>
            </Link>
          ) : (
            <Link to="/register">
              <button className="border-[3px] border-[#22C55E] text-[#22C55E] rounded-full px-12 py-4 text-lg font-bold tracking-tight active:bg-[#22C55E]/10 transition-colors" data-testid="splash-signup-btn">
                Sign Up Now
              </button>
            </Link>
          )}
          {!isAuthenticated && (
            <p className="mt-5 text-sm text-white/50">
              Already have an account? <Link to="/login" className="text-[#22C55E] font-semibold hover:underline">Sign in</Link>
            </p>
          )}
        </div>
      </section>

      {/* Hero Banner */}
      <HeroBanner isAuthenticated={isAuthenticated} />

      {/* Crowdfunding Hero — gated by feature flag */}
      <FeatureGate flag="crowdfund_hero">
        <CrowdfundHero compact />
      </FeatureGate>

      {/* Features Grid */}
      <section className="py-20 bg-[#0A0A0A]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#22C55E] mb-3">Platform Features</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>Built for creators, freelancers & entrepreneurs</h2>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 max-w-3xl mx-auto">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="group bg-[#0d150e] border border-[#22C55E]/10 rounded-2xl p-5 sm:p-6 transition-all duration-300 ease-out hover:border-[#22C55E]/40 hover:-translate-y-1 hover:shadow-[0_0_0_1px_rgba(34,197,94,0.12),0_24px_48px_rgba(0,0,0,0.5)]"
                data-testid={`feature-card-${f.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="mb-5 sm:mb-6 h-16 sm:h-20 flex items-end transition-transform duration-300 ease-out group-hover:scale-[1.06]">
                  <f.icon className="w-14 h-14 sm:w-16 sm:h-16 text-[#22C55E]" weight="duotone" />
                </div>
                <h3 className="text-white font-bold text-base sm:text-lg mb-1.5 leading-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>{f.title}</h3>
                <p className="text-[#999999] text-xs sm:text-[13px] leading-snug">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>Ready to get started?</h2>
          <p className="text-[#999999] mb-8 max-w-md mx-auto">Join creators and entrepreneurs managing their payments and growing their business with Divispend.</p>
          <Link to={isAuthenticated ? "/dashboard" : "/register"}>
            <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm h-12 px-8 text-base font-semibold" data-testid="cta-button">
              {isAuthenticated ? "Go to Dashboard" : "Create free account"} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#222222] py-12 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-10 mb-10">
            <div className="space-y-3 max-w-xs">
              <img src={LOGO_URL} alt="Divispend" className="h-9 w-auto" />
              <p className="text-sm text-[#999999] leading-relaxed">
                Built for a world where one paycheck isn&apos;t enough.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 sm:gap-12">
              <div>
                <p className="text-xs tracking-[0.18em] uppercase font-bold text-white mb-3" style={{ fontFamily: 'Cabinet Grotesk' }}>Company</p>
                <ul className="space-y-2.5">
                  <li><Link to="/about" className="text-sm text-[#999999] hover:text-[#22C55E] transition-colors" data-testid="footer-about-link">About Us</Link></li>
                  <li><Link to="/team" className="text-sm text-[#999999] hover:text-[#22C55E] transition-colors" data-testid="footer-team-link">Our Team</Link></li>
                  <li><Link to="/features" className="text-sm text-[#999999] hover:text-[#22C55E] transition-colors" data-testid="footer-features-link">Features</Link></li>
                  <li><Link to="/trust" className="text-sm text-[#999999] hover:text-[#22C55E] transition-colors" data-testid="footer-trust-link">Trust &amp; Security</Link></li>
                  <li><Link to="/terms" className="text-sm text-[#999999] hover:text-[#22C55E] transition-colors" data-testid="footer-terms-link">Terms of Service</Link></li>
                </ul>
              </div>
              <div>
                <p className="text-xs tracking-[0.18em] uppercase font-bold text-white mb-3" style={{ fontFamily: 'Cabinet Grotesk' }}>Get Started</p>
                <ul className="space-y-2.5">
                  <li><Link to="/register" className="text-sm text-[#999999] hover:text-[#22C55E] transition-colors" data-testid="footer-register-link">Create Account</Link></li>
                  <li><Link to="/login" className="text-sm text-[#999999] hover:text-[#22C55E] transition-colors" data-testid="footer-login-link">Sign In</Link></li>
                  <li><Link to="/explore" className="text-sm text-[#999999] hover:text-[#22C55E] transition-colors" data-testid="footer-explore-link">Explore Marketplace</Link></li>
                </ul>
              </div>
              <div>
                <p className="text-xs tracking-[0.18em] uppercase font-bold text-white mb-3" style={{ fontFamily: 'Cabinet Grotesk' }}>Legal</p>
                <ul className="space-y-2.5">
                  <li><Link to="/privacy_policy" className="text-sm text-[#999999] hover:text-[#22C55E] transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="border-t border-[#222222] pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-[#666666]">&copy; 2026 Divispend. All rights reserved.</p>
            <p className="text-xs text-[#666666]">Built with care for everyone running a side hustle.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
